import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface DirtyGuardResult {
  /** True when the guard has blocked a nav and is waiting on the user. Host
   *  page uses this to open <NavigateAwayWarning>. */
  pendingNavigation: boolean;
  /** Proceed with the pending navigation. */
  confirmLeave: () => void;
  /** Cancel the pending navigation. */
  cancelLeave: () => void;
}

/**
 * Warns on navigate-away when `dirty` is true.
 *
 * App runs on legacy <BrowserRouter>, which does NOT support React Router's
 * useBlocker (data router only). This hook intercepts navigation via DOM event
 * listeners and signals the host page to render an inline
 * <NavigateAwayWarning> — no more `window.confirm()`.
 *
 * Spec ENTITY_CREATION_WIZARD_SPEC.md §14.3.
 */
export function useDirtyGuard(dirty: boolean): DirtyGuardResult {
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location.pathname);
  locationRef.current = location.pathname;

  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Browser close/refresh — still native beforeunload because the OS owns that moment.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // In-app <a href> clicks — intercept at document level, defer to host UI.
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!dirtyRef.current) return;
      const anchor = (e.target as HTMLElement | null)?.closest('a[href]') as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('#') || href.startsWith('mailto:')) return;
      if (href === locationRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      setPendingNavigation(href);
    };
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);

  // Back/forward — confirm or re-push.
  useEffect(() => {
    const onPop = () => {
      if (!dirtyRef.current) return;
      // popstate already happened; the only way to "cancel" is to push the
      // current URL back. Use the inline warning: while it's open, we put the
      // URL back; if user confirms Leave, they have to navigate again.
      window.history.pushState(null, '', locationRef.current);
      setPendingNavigation(locationRef.current /* placeholder — actual target is unknown */);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const confirmLeave = useCallback(() => {
    const target = pendingNavigation;
    setPendingNavigation(null);
    if (target && target !== locationRef.current) {
      // Mark as clean so the nav doesn't re-trigger the guard
      dirtyRef.current = false;
      navigate(target);
    }
  }, [navigate, pendingNavigation]);

  const cancelLeave = useCallback(() => {
    setPendingNavigation(null);
  }, []);

  return { pendingNavigation: pendingNavigation !== null, confirmLeave, cancelLeave };
}
